import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ArrowLeft, MagnifyingGlass, BookmarkSimple, Heart, BookOpen } from "@phosphor-icons/react";
import SortSelect, { LIST_SORT_OPTIONS } from "@/components/SortSelect";

const CONFIG = {
  collection: { title: "Coleção", color: "#E63946", Icon: BookmarkSimple },
  wishlist: { title: "Desejos", color: "#7209B7", Icon: Heart },
  read: { title: "Já Lidos", color: "#10B981", Icon: BookOpen },
};

export default function MemberMangaList() {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [member, setMember] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("added_recent");
  const [loading, setLoading] = useState(true);
  const cfg = CONFIG[type];

  useEffect(() => {
    if (!cfg) { navigate(`/membros/${id}`); return; }
    setLoading(true);
    Promise.all([
      api.get(`/members/${id}`).then(r => setMember(r.data)),
      api.get(`/members/${id}/manga`, { params: { list_type: type, sort } }).then(r => setItems(r.data)),
    ]).catch(() => navigate(`/membros/${id}`))
      .finally(() => setLoading(false));
  }, [id, type, cfg, navigate, sort]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const lq = q.toLowerCase();
    return items.filter(m =>
      m.title?.toLowerCase().includes(lq) ||
      m.author?.toLowerCase().includes(lq) ||
      m.publisher?.toLowerCase().includes(lq) ||
      m.isbn?.toLowerCase().includes(lq)
    );
  }, [items, q]);

  if (loading) return <div className="pt-32 text-center text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>;
  if (!cfg || !member) return null;
  const { title, color, Icon } = cfg;

  return (
    <div className="pt-20 max-w-6xl mx-auto px-5 lg:px-8 pb-24" data-testid="member-list-page">
      <Link to={`/membros/${id}`} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6">
        <ArrowLeft size={16} weight="bold" /> Voltar ao perfil de {member.name}
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <Icon size={26} weight="fill" style={{ color }} />
        <h1 className="font-display text-4xl font-extrabold tracking-tight">{title} de {member.name}</h1>
        <span className="ml-2 font-mono-accent text-sm text-zinc-500">{items.length} mangás</span>
      </div>

      <div className="my-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Pesquisar..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 bg-[#121217] border-white/10 h-11" data-testid="member-list-search" />
        </div>
        <SortSelect value={sort} onChange={setSort} options={LIST_SORT_OPTIONS} testid="member-list-sort" />
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center">
          <div className="font-display font-bold text-xl">{items.length === 0 ? `Sem mangás em "${title}"` : "Sem resultados"}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
          {filtered.map(m => (
            <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="group" data-testid={`member-list-item-${m.manga_id}`}>
              <div className="aspect-[2/3] overflow-hidden rounded-xl bg-[#121217] border border-white/5">
                {m.cover_url ? <img src={m.cover_url} alt={m.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" /> : <div className="w-full h-full grid place-items-center text-zinc-700 text-xs">SEM CAPA</div>}
              </div>
              <div className="mt-3 font-display font-semibold text-sm line-clamp-2">{m.title}</div>
              {m.author && <div className="font-mono-accent text-[10px] text-zinc-500 mt-1">{m.author}</div>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
